import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import dayjs from 'dayjs'
import useToastContext from 'hooks/useToastContext'
import { exportReportToExcel } from 'utils/reportExport'
import { SummaryCard, LoadingState, EmptyState } from './ScheduledVsActualReport'

// ═══ CHART.JS IMPORTS ═══
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement, // Required for Bubble
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Bubble } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Tooltip,
  Legend
)

// ═══ PAGINATION CONSTANTS ═══
const ITEMS_PER_PAGE = 10;

const EmployeeHoursSummaryReport = ({ selectedEmployees, dateRange, onExportReady }) => {
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const notification = useToastContext()

  // ═══ PAGINATION STATE ═══
  const [currentPage, setCurrentPage] = useState(1)
  
  // ═══ REFS ═══
  const isRegistered = useRef(false);
  const isExportingRef = useRef(false);

  useEffect(() => {
    const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
    const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
    fetchData(start, end, currentPage)
  }, [selectedEmployees, dateRange, currentPage])

  // ═══ RESET PAGE TO 1 WHENEVER FILTER CHANGES ═══
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedEmployees, dateRange])

  const fetchData = async (start, end, page) => {
    setIsLoading(true)
    try {
      const employeeIDs = selectedEmployees.map(e => e.value)
      const payload = {
        startDate: start,
        endDate: end,
        page: page,
        limit: ITEMS_PER_PAGE,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      const result = await apiGet(apiPath.reportEmployeeHours, payload)
      
      if (result?.data?.success) {
        let finalData = []
        const rawResults = result.data.results
        
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          finalData = rawResults.results
          setTotalRecords(rawResults.total || 0)
        } else if (Array.isArray(rawResults)) {
          finalData = rawResults
          setTotalRecords(rawResults.length)
        }
        setData(finalData || [])
      } else {
        setData([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching employee hours report:', error)
      notification.error('Error fetching report data')
      setData([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }

  // ═══ HANDLE EXPORT ═══
  const handleExport = useCallback(async () => {
    if (isExportingRef.current) return;
    
    isExportingRef.current = true;
    setIsExporting(true);
    
    try {
      const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
      const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
      const employeeIDs = selectedEmployees.map(e => e.value)
      
      const payload = {
        startDate: start,
        endDate: end,
        export: true,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      
      const result = await apiGet(apiPath.reportEmployeeHours, payload)
      
      if (result?.data?.success) {
        let allData = []
        const rawResults = result.data.results
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          allData = rawResults.results
        } else if (Array.isArray(rawResults)) {
          allData = rawResults
        }
        
        if (allData.length > 0) {
          exportReportToExcel(allData, 'Employee Hours Summary', 'employee_hours')
        } else {
          notification.error('No data available to export')
        }
      } else {
        notification.error('Failed to fetch all data for export')
      }
    } catch (error) {
      console.error('Export failed:', error)
      notification.error('Export failed')
    } finally {
      setIsExporting(false)
      isExportingRef.current = false;
    }
  }, [dateRange, selectedEmployees, notification])

  // ═══ REGISTER EXPORT FUNCTION WITH PARENT ═══
  useEffect(() => {
    if (onExportReady && !isRegistered.current && data.length > 0) {
      onExportReady(handleExport);
      isRegistered.current = true;
    }
    
    return () => {
      if (onExportReady && isRegistered.current) {
        onExportReady(null);
        isRegistered.current = false;
      }
    }
  }, [handleExport, onExportReady, data]);

  // ═══ PAGINATION CALCULATIONS ═══
  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE)

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  // ═══ EARLY RETURNS ═══
  if (isLoading) return <LoadingState />;
  if (!data.length && !isLoading) return <EmptyState />;

  // Calculate logic-heavy Summary Cards
  const totalRegular = data.reduce((sum, r) => sum + (r.totalRegularHours || 0), 0)
  const totalOT = data.reduce((sum, r) => sum + (r.totalOvertimeHours || 0), 0)
  const totalDaysPresent = data.reduce((sum, r) => sum + (r.daysPresent || 0), 0)
  
  // Company Average Daily Hours
  const totalAllHours = totalRegular + totalOT
  const avgDailyHours = totalDaysPresent > 0 ? (totalAllHours / totalDaysPresent).toFixed(1) : 0

  // ═══ CHART 1 DATA: HOURS BREAKDOWN (HORIZONTAL BAR) ═══
  const chart1Labels = data.map(item => item.employeeName || 'Unknown');
  const chart1RegularData = data.map(item => item.totalRegularHours || 0);
  const chart1OTData = data.map(item => item.totalOvertimeHours || 0);

  const hoursBarChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: { label: (context) => `${context.dataset.label}: ${context.parsed.x.toFixed(1)}h` }
      }
    },
    scales: {
      x: { stacked: false, grid: { display: false }, ticks: { callback: (val) => val + 'h', font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { font: { size: 10 } } }
    }
  };

  const hoursBarChartData = {
    labels: chart1Labels,
    datasets: [
      {
        label: 'Regular Hours',
        data: chart1RegularData,
        backgroundColor: '#2563EB',
        borderColor: '#1E40AF',
        borderWidth: 1,
        borderRadius: 3,
      },
      {
        label: 'OT Hours',
        data: chart1OTData,
        backgroundColor: '#F97316',
        borderColor: '#EA580C',
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };

  // ═══ CHART 2 DATA: BUBBLE CHART ═══
  // X-Axis: Days Present | Y-Axis: Regular Hours | Size: Avg Hours per Day
  const bubbleData = data.map(item => {
    const avgHoursPerDay = item.daysPresent > 0 
      ? ((item.totalRegularHours || 0) + (item.totalOvertimeHours || 0)) / item.daysPresent 
      : 0;

    return {
      label: item.employeeName || 'Unknown',
      data: [{
        x: item.daysPresent || 0,
        y: item.totalRegularHours || 0,
        r: Math.max(avgHoursPerDay * 3, 5) // Scale factor to make bubbles visible
      }]
    };
  });

  const bubbleChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend because labels are on tooltips
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.raw._customLabel || context.dataset.label || '';
            return [
              `${label}`,
              `Days Present: ${context.parsed.x}d`,
              `Regular Hrs: ${context.parsed.y.toFixed(1)}h`,
              `Avg Hrs/Day: ${(context.raw.r / 3).toFixed(1)}h`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        grid: { color: '#f3f4f6' },
        title: {
          display: true,
          text: 'Days Present',
          color: '#6B7280',
          font: { size: 11, weight: 'bold' }
        },
        ticks: { stepSize: 1, callback: (val) => val + 'd', font: { size: 10 } }
      },
      y: {
        grid: { color: '#f3f4f6' },
        title: {
          display: true,
          text: 'Regular Hours',
          color: '#6B7280',
          font: { size: 11, weight: 'bold' }
        },
        ticks: { callback: (val) => val + 'h', font: { size: 10 } }
      }
    }
  };

  const bubbleChartData = {
    datasets: bubbleData.map((item, index) => ({
      label: item.label,
      data: item.data,
      backgroundColor: `hsla(${index * 40}, 70%, 50%, 0.7)`, // Generates distinct colors
      borderColor: `hsla(${index * 40}, 70%, 50%, 1)`,
      borderWidth: 1,
      hoverBackgroundColor: `hsla(${index * 40}, 70%, 50%, 0.9)`,
    }))
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className='grid grid-cols-3 gap-4 mb-6'>
        <SummaryCard label='Total Regular Hours' value={`${totalRegular.toFixed(1)}h`} color='blue' />
        <SummaryCard label='Total Overtime Hours' value={`${totalOT.toFixed(1)}h`} color='orange' />
        <SummaryCard label='Avg. Hours / Day' value={`${avgDailyHours}h`} color='green' />
      </div>

      {/* ═══ CHARTS SECTION (2 Rows) ═══ */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
        
        {/* Chart 1: Employee Hours Breakdown */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Hours Per Employee</h3>
          <div className='h-64 w-full'>
            <Bar options={hoursBarChartOptions} data={hoursBarChartData} />
          </div>
        </div>

        {/* Chart 2: Bubble Chart (Days vs Hours vs Intensity) */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Workload & Attendance Insights</h3>
          <div className='h-64 w-full'>
            <Bubble options={bubbleChartOptions} data={bubbleChartData} />
          </div>
        </div>
      </div>

      {/* ═══ TABLE ═══ */}
      <div className='overflow-x-auto rounded-lg border border-[#E1E6EE]'>
        <table className='w-full text-xs text-left text-[#6c6c6c]'>
          <thead className='text-xs text-gray-900 bg-[#EBEFF8]'>
            <tr>
              <th className='py-3 px-4 font-medium'>Sr no.</th>
              <th className='py-3 px-4 font-medium'>Employee</th>
              <th className='py-3 px-4 font-medium'>Days Present</th>
              <th className='py-3 px-4 font-medium'>Regular Hrs</th>
              <th className='py-3 px-4 font-medium'>OT Hrs</th>
              <th className='py-3 px-4 font-medium'>Total Hrs</th>
              <th className='py-3 px-4 font-medium'>Avg Hrs/Day</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const totalHours = (row.totalRegularHours || 0) + (row.totalOvertimeHours || 0)
              const avgPerDay = row.daysPresent > 0 ? (totalHours / row.daysPresent).toFixed(1) : 0
              return (
                <tr key={i} className='bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                  <td className='py-3 px-4 font-medium text-gray-800'>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                  <td className='py-3 px-4 font-medium text-gray-800'>
                    <div>{row.employeeName}</div>
                    <div className='text-gray-400 text-xs'>{row.employeeId}</div>
                  </td>
                  <td className='py-3 px-4 text-gray-600'>{row.daysPresent || 0}</td>
                  <td className='py-3 px-4 text-gray-600'>{(row.totalRegularHours || 0).toFixed(1)}h</td>
                  <td className='py-3 px-4 text-orange-500 font-medium'>
                    {(row.totalOvertimeHours || 0).toFixed(1)}h
                  </td>
                  <td className='py-3 px-4 font-semibold text-[#2563EB]'>{totalHours.toFixed(1)}h</td>
                  <td className='py-3 px-4 text-gray-600'>{avgPerDay}h</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ═══ PAGINATION FOOTER ═══ */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-4 pt-2 me-8 border-t border-gray-100'>
          <div className='text-sm text-gray-500'>
            Total <span className='font-medium text-gray-700'>{totalRecords}</span> Records
          </div>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => currentPage === 1 ? null : handlePageChange(currentPage - 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors border ${
                currentPage === 1 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Prev
            </button>
            
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-8 h-8 rounded text-sm font-medium flex items-center border justify-center transition-colors ${
                    currentPage === pageNum
                      ? 'bg-[#1E40AF] text-white'
                      : 'text-gray-600 bg-transparent hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
            
            <button
              onClick={() => currentPage === totalPages ? null : handlePageChange(currentPage + 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                currentPage === totalPages 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeeHoursSummaryReport